import type {
  JwtPayload,
  User as SupabaseUser,
  UserAppMetadata,
  UserMetadata,
} from "@supabase/supabase-js"

import type { Prettify } from "@repo/utils/types"

export type ExtendedUserAppMetadata = Prettify<
  UserAppMetadata & {
    app_role?: { id: number; name: string }
    email_verified: boolean
  }
>

export type ExtendedUserMetadata = Prettify<
  UserMetadata & {
    name?: string
    avatar_url?: string
    requires_password_reset?: boolean
  }
>

export type AuthUser = {
  id: string
  aal: string
  email?: string
  phone?: string
  is_anonymous: boolean
  app_metadata: ExtendedUserAppMetadata
  user_metadata: ExtendedUserMetadata
}

export const mapSupabaseUserToAuthUser = (user: SupabaseUser): AuthUser => {
  const isEmailVerified = user.email_confirmed_at !== null
  const appMetadata = user.app_metadata as ExtendedUserAppMetadata

  return {
    id: user.id,
    aal: user.app_metadata.aal,
    email: user.email,
    phone: user.phone,
    is_anonymous: user.role === "anon",
    // We have to patch the app_metadata to include the email_verified field since it is not included in the user object
    // when returned directly from the database and not inferred from the claims/JWT
    app_metadata: {
      ...appMetadata,
      email_verified: isEmailVerified,
    },
    user_metadata: user.user_metadata as ExtendedUserMetadata,
  }
}

export const mapSupabaseClaimsToAuthUser = (claims: JwtPayload): AuthUser => {
  return {
    id: claims.sub,
    aal: claims.aal,
    email: claims.email,
    phone: claims.phone,
    is_anonymous: claims.is_anonymous ?? true,
    app_metadata: (claims.app_metadata ?? {
      email_verified: false,
    }) as ExtendedUserAppMetadata,
    user_metadata: (claims.user_metadata ?? {}) as ExtendedUserMetadata,
  }
}
